// Copyright (c) Ping. All rights reserved.
//
// Thin shared wrapper around `react-native-linear-gradient`.
//
// Tailwind's `bg-gradient-to-*`, `from-*`, `to-*`, `via-*` utilities
// don't compile through NativeWind because React Native has no native
// gradient primitive — every Tailwind gradient utility silently
// becomes a no-op on RN. The community-blessed escape hatch is the
// `react-native-linear-gradient` package: a native CAGradientLayer/
// `android.graphics.LinearGradient` on the native side; on web we
// alias it (in `apps/web/vite.config.ts`) to a DOM `<div>` shim that
// emits a CSS `linear-gradient(...)` backgroundImage.
//
// Use this component wherever a shared v2 surface needs a gradient.
// The Tailwind class still goes on a child View if needed — most of
// the time the gradient props alone replace the bg-gradient utility.

import type { PropsWithChildren } from 'react';
import RNLinearGradient from 'react-native-linear-gradient';
import type { StyleProp, ViewStyle } from 'react-native';

export interface LinearGradientProps {
  /** Color stops, CSS color strings or `#RRGGBB` hex. */
  readonly colors: readonly string[];
  /** Optional 0..1 stops; must match `colors.length` if provided. */
  readonly locations?: readonly number[];
  /** Gradient origin (0..1 in both axes). Default `{ x: 0.5, y: 0 }`. */
  readonly start?: { readonly x: number; readonly y: number };
  /** Gradient end (0..1 in both axes). Default `{ x: 0.5, y: 1 }`. */
  readonly end?: { readonly x: number; readonly y: number };
  /** Alternative angle-based direction (degrees, 0 = top). */
  readonly useAngle?: boolean;
  readonly angle?: number;
  /** Forwarded style for the wrapping container. */
  readonly style?: StyleProp<ViewStyle>;
  /** NativeWind className for the wrapping container. */
  readonly className?: string;
}

export function LinearGradient(
  props: PropsWithChildren<LinearGradientProps>,
): JSX.Element {
  const {
    colors,
    locations,
    start,
    end,
    useAngle,
    angle,
    style,
    className,
    children,
  } = props;

  // `react-native-linear-gradient`'s prop type is invariant on
  // `colors`/`locations`; cast to widen to readonly inputs.
  return (
    <RNLinearGradient
      colors={colors as unknown as string[]}
      {...(locations !== undefined
        ? { locations: locations as unknown as number[] }
        : {})}
      {...(start !== undefined ? { start } : {})}
      {...(end !== undefined ? { end } : {})}
      {...(useAngle !== undefined ? { useAngle } : {})}
      {...(angle !== undefined ? { angle } : {})}
      {...(style !== undefined ? { style } : {})}
      {...(className !== undefined ? { className } : {})}
    >
      {children}
    </RNLinearGradient>
  );
}
